import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled, useColorScheme } from '@mui/material/styles';
import ForgotPassword from '../../components/dialogs/ForgotPassword';
import AppTheme from '../../shared-theme/AppTheme';
import ColorModeSelect from '../../shared-theme/ColorModeSelect';
import { GoogleIcon } from '../../components/CustomIcons';
import logoCabanaWhite from '../../images/logonextcabana-white.svg'
import logoCabanaBlack from '../../images/logonextcabana-dark.svg'
import { useState, useEffect } from 'react';
import { auth, entrarComGoogle, loginComEmailESenha } from '../../../contexts/authentication';
import { useNavigate } from 'react-router-dom';
import WarningAlert from '../../components/WarningAlert';
import PasswordInput from '../../components/PasswordInput';
import EmailInput from '../../components/EmailInput';
import SuccessAlert from '../../components/SucessAlert';
import Loading from '../../components/Loading';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  maxHeight: '90vh',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(0, 0%, 0%, 0.05) 0px 5px 15px 0px, hsla(0, 0%, 0%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(0, 0%, 0%, 0.5) 0px 5px 15px 0px, hsla(0, 0%, 0%, 0.08) 0px 15px 35px -5px',
  }),
  '@media (orientation: landscape)': {
    overflowY: 'auto',
  },
}));

// ALTERAÇÃO: Trocamos o gradiente de fundo azul por um verde, de acordo com o tema.
const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100dvh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    // Modo claro: gradiente verde bem claro para o fundo off-white
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsla(115, 78%, 91%, 1.00), #fffcf2)',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      // Modo escuro: gradiente verde escuro para o fundo preto
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(121, 73%, 13%, 0.50), #0e0e0e)',
    }),
  },
}));

export default function SignIn(props: { disableCustomTheme?: boolean }) {
  return (
    <AppTheme {...props}>
      <SignInContent />
    </AppTheme>
  )
}

function SignInContent() {
  const { mode, systemMode } = useColorScheme();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [successAlert, setSuccessAlert] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate("/dashboard"); // Redireciona se o usuário estiver logado
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe(); // Limpa o listener ao desmontar o componente
  }, []);

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(false)
        setAlertMessage('')
      }, 6000)
    }
  }, [alert])

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email || !password) {
      setAlert(true)
      setAlertMessage('Preencha todos os campos')
      return;
    }

    try {
      await loginComEmailESenha(email, password);
      navigate("/dashboard");
    } catch (error: any) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setAlert(true)
        setAlertMessage('Senha ou email incorreto. Por favor, verifique e tente novamente')
      } else if (error.code === 'auth/user-not-found') {
        setAlert(true)
        setAlertMessage('Usuário não encontrado. Verifique o e-mail informado.')
      } else {
        setAlert(true)
        setAlertMessage('Erro ao fazer login. Tente novamente.')
      }
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
      <SignInContainer direction="column" justifyContent="space-between">
        <Card variant="outlined" sx={{
          gap: 'calc(1 * var(--template-spacing))',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={mode === 'dark' || systemMode === 'dark' ? logoCabanaWhite : logoCabanaBlack}
              alt="logo-cabanalink"
              style={{ width: '200px', height: 'auto', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
          </div>
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(1rem, 10vw, 1.15rem)' }}
          >
            Entrar
          </Typography>
          <Box
            component="form"
            onSubmit={handleSignIn}
            noValidate
            sx={{
              display: 'flex',
              fontFamily: 'Roboto, sans-serif',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            <FormControl>
              <EmailInput email={email} setEmail={setEmail} />
            </FormControl>
            <FormControl>
              <PasswordInput
                password={password}
                setPassword={setPassword}
                labelMessage="Senha"
              />
            </FormControl>
            <ForgotPassword
              open={open}
              handleClose={handleClose}
              setSuccessAlert={setSuccessAlert}
              setAlertMessage={setAlertMessage}
              setAlert={setAlert}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
            >
              Entrar
            </Button>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              Esqueceu sua senha?
            </Link>
          </Box>
          <Divider>ou</Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => entrarComGoogle()}
              startIcon={<GoogleIcon />}
            >
              Entrar com Google
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
              Não tem uma conta?{' '}
              <Link
                href="/signup"
                variant="body2"
                sx={{ alignSelf: 'center' }}
              >
                Cadastrar-se
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignInContainer>
      {alert && (
        <WarningAlert message={alertMessage} />
      )}
      {successAlert && (
        <SuccessAlert message={alertMessage} />
      )}
    </>
  );
}